import { FieldMetadataDetail, LookupEntityList, ParsedFieldMetadataDetail } from '../../../../insa-models';
import {
  FieldBase,
  createFilePickerField,
  createMailButtonField,
  createTextboxField,
  createEntityPickerField,
  createDropdownField,
  createDatePickerField,
  createCheckboxField,
} from '../models/Field';

// `MetaDataDetails[]` -> `Field[]`
export function InsaFieldsToCustomFields(
  insaFields: ParsedFieldMetadataDetail[],
  lookups: LookupEntityList = null,
  existingItem: any = null
): FieldBase[] {
  return createCustomFields(insaFields, lookups, existingItem).sort((a, b) => {
    if (a.order > b.order) return 1;
    if (a.order < b.order) return -1;
    else return 0;
  });
}

// ▼▼▼ HELPER FUNCTIONS BELOW ▼▼▼
function createCustomFields(
  insaFields: FieldMetadataDetail[],
  lookups: LookupEntityList,
  existingItem?: any
) {
  insaFields = mergeRegularFieldsWithEditingFields(insaFields);
  return insaFields.map((insaF) =>
    insaFToCustomF(insaF, lookups, existingItem)
  );
}

function mergeRegularFieldsWithEditingFields(
  insaFields: FieldMetadataDetail[]
): FieldMetadataDetail[] {
  const specialFields = insaFields.filter((insaF) => insaF.EditingFieldName); // fields that need to be replaced with appropriate EditingField counterpart
  return insaFields
    .filter((insaF) => !insaF.EditingFieldName)
    .map((insaF) => {
      const specialF = specialFields.filter(
        (specialF) => specialF.EditingFieldName === insaF.FieldName
      )[0];
      if (specialF) {
        return {
          ...insaF,
          OrderID: specialF.OrderID,
          RenderingGroupName: specialF.RenderingGroupName,
          VisibleOnGrid: specialF.VisibleOnGrid,
        } as FieldMetadataDetail;
      }
      return insaF;
    });
}

function insaFToCustomF(
  insaF: FieldMetadataDetail,
  lookups: LookupEntityList,
  existingItem?: any
) {
  return createNewField(insaF, lookups, existingItem);
}

function computeItems(
  fieldData: FieldMetadataDetail,
  lookups: LookupEntityList
): any[] {
  if (fieldData.LookupTable) {
    const lookupEntity = lookups.find(
      (entity) => fieldData.FieldName === entity.MetadataFieldname
    );
    const lookupText = lookupEntity.lookupDisplayField;
    const lookupValue = lookupEntity.lookupField;
    return lookupEntity.lookupData.map((item) => {
      const itemResult: any = {};
      itemResult.label = item[lookupText];
      itemResult.value = item[lookupValue];
      return itemResult;
    });
  }

  // if no LookupTable, return undefined
  return;
}

function createNewField(
  insaF: ParsedFieldMetadataDetail,
  lookups = null,
  existingItem = null
) {
  if (insaF.LookupTable) {
    return createDropdownField({
      key: insaF.FieldName,
      label: insaF.FieldTitle,
      value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
      items: computeItems(insaF, lookups),
      placeholder: insaF.DefaultValue,
      dataType: insaF.JavascriptType,
      order: insaF.OrderID,
      isLocked: insaF.Locked,
      size: insaF.FieldWidth,
      groupName: insaF.RenderingGroupName,
      isHidden: !insaF.VisibleOnGrid,
    });
  } else if (insaF.Format) {
    const FORMAT = insaF.Format.toUpperCase();
    if (FORMAT === 'MAILBUTTON') {
      return createMailButtonField({
        key: insaF.FieldName,
        label: insaF.FieldTitle,
        value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
        placeholder: insaF.DefaultValue,
        dataType: insaF.JavascriptType,
        order: insaF.OrderID,
        isLocked: insaF.Locked,
        size: insaF.FieldWidth,
        groupName: insaF.RenderingGroupName,
        isHidden: !insaF.VisibleOnGrid,
      });
    } else if (FORMAT === 'SELECTOR') {
      if (insaF.FieldName.toLowerCase().indexOf('file') !== -1) {
        const initialName = existingItem ? existingItem[insaF.FieldName] : '';
        return createFilePickerField({
          key: insaF.FieldName,
          label: insaF.FieldTitle,
          file: { initialName, currName: initialName },
          placeholder: insaF.DefaultValue,
          dataType: insaF.JavascriptType,
          order: insaF.OrderID,
          isLocked: insaF.Locked,
          size: insaF.FieldWidth,
          groupName: insaF.RenderingGroupName,
          isHidden: !insaF.VisibleOnGrid,
        });
      } else {
        return createEntityPickerField({
          key: insaF.FieldName,
          label: insaF.FieldTitle,
          value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
          placeholder: insaF.DefaultValue,
          dataType: insaF.JavascriptType,
          order: insaF.OrderID,
          isLocked: insaF.Locked,
          size: insaF.FieldWidth,
          groupName: insaF.RenderingGroupName,
          isHidden: !insaF.VisibleOnGrid,
        });
      }
    }
  }

  switch (insaF.DataType) {
    case 'int':
    case 'smallint':
    case 'tinyint':
    case 'bigint': {
      const useGrouping = insaF.Format.indexOf(',') !== -1;
      const usePercentageSuffix = insaF.Format.trim().slice(-1) === '%' ? true : false;
      return createTextboxField({
        key: insaF.FieldName,
        label: insaF.FieldTitle,
        value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
        placeholder: insaF.DefaultValue,
        fractionalLength: 0,
        useGrouping,
        usePercentageSuffix,
        dataType: insaF.JavascriptType,
        order: insaF.OrderID,
        isLocked: insaF.Locked,
        size: insaF.FieldWidth,
        groupName: insaF.RenderingGroupName,
        isHidden: !insaF.VisibleOnGrid,
        prefixFromFields: insaF.PrefixColumns ? insaF.PrefixColumns.split(',') : null
      });
    }
    case 'money':
    case 'real':
    case 'float':
    case 'decimal':
    case 'short':
    case 'double': {
      const useGrouping = insaF.Format.indexOf(',') !== -1;
      const usePercentageSuffix = insaF.Format.trim().slice(-1) === '%' ? true : false;
      return createTextboxField({
        key: insaF.FieldName,
        label: insaF.FieldTitle,
        value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
        placeholder: insaF.DefaultValue,
        fractionalLength: 2,
        useGrouping,
        usePercentageSuffix,
        dataType: insaF.JavascriptType,
        order: insaF.OrderID,
        isLocked: insaF.Locked,
        size: insaF.FieldWidth,
        textAlign: 'right',
        groupName: insaF.RenderingGroupName,
        isHidden: !insaF.VisibleOnGrid,
        prefixFromFields: insaF.PrefixColumns ? insaF.PrefixColumns.split(',') : null
      });
    }
    case 'bit':
      return createCheckboxField({
        key: insaF.FieldName,
        label: insaF.FieldTitle,
        value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
        placeholder: insaF.DefaultValue,
        dataType: insaF.JavascriptType,
        order: insaF.OrderID,
        isLocked: insaF.Locked,
        size: insaF.FieldWidth,
        groupName: insaF.RenderingGroupName,
        isHidden: !insaF.VisibleOnGrid,
      });
    case 'datetime':
    case 'timestamp':
    case 'smalldatetime':
      return createDatePickerField({
        key: insaF.FieldName,
        label: insaF.FieldTitle,
        formatString: insaF.Format,
        value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
        placeholder: insaF.DefaultValue,
        dataType: insaF.JavascriptType,
        order: insaF.OrderID,
        isLocked: insaF.Locked,
        size: insaF.FieldWidth,
        groupName: insaF.RenderingGroupName,
        isHidden: !insaF.VisibleOnGrid,
      });
    case 'nvarchar':
    case 'varchar':
    case 'char':
    case 'ntext':
    case 'string':
    default:
      return createTextboxField({
        key: insaF.FieldName,
        label: insaF.FieldTitle,
        value: existingItem ? existingItem[insaF.FieldName] : insaF.InitValue,
        placeholder: insaF.DefaultValue,
        fractionalLength: 0,
        dataType: insaF.JavascriptType,
        order: insaF.OrderID,
        isLocked: insaF.Locked,
        size: insaF.FieldWidth,
        groupName: insaF.RenderingGroupName,
        isHidden: !insaF.VisibleOnGrid,
        prefixFromFields: insaF.PrefixColumns ? insaF.PrefixColumns.split(',') : null
      });
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-generic-form/generic-editor/utilities/InsaFieldsToCustomFields.ts